import { ref, watch, computed } from "@vue/composition-api";
import axios from "@/libs/axios";
import store from "@/store";
import { debounce } from "lodash";

export default function useList() {
  const tableColumns = [
    { key: "actions", label: "actions.name", sortable: false },
    { key: "reference_number", label: "security_self_audit.reference_number", sortable: true },
    { key: "unit.description", label: "unit.title", sortable: false },
    { key: "rems", label: "REMS", sortable: false },
    { key: "sirh", label: "SIRH", sortable: false },
    { key: "last_status.name", label: "security_self_audit.status", sortable: false },
    { key: "in_review_by", label: "risk.status_assigned", sortable: false },
    { key: "assigned_to", label: "risk.status_in_review", sortable: false },
    { key: "accepted_or_cancelled_by", label: "risk.status_approved_cancelled", sortable: false },
    { key: "formAnswer.template.description", label: "risk.template.title", sortable: false },
    { key: "created_at", label: "date.created_at", sortable: true, formatter: "formatDateAssigned" },
  ];

  // Preload select sources (same as risk)
  store.dispatch("unit/getAll");
  store.dispatch("country/getAll");
  store.dispatch("municipality/getAll");
  store.dispatch("user/getAll");

  const unitOptions = computed(() => store.getters["unit/getSelectedItems"]);
  const countryOptions = computed(() => store.getters["country/getSelectedItems"]);
  const municipalityOptions = computed(() => store.getters["municipality/getSelectedItems"]);
  const userOptions = computed(() => store.getters["user/getSelectedItems"]);


  // Pagination/sorting/search
  const perPage = ref(10);
  const currentPage = ref(1);
  const totalList = ref(0);
  const searchQuery = ref("");
  const sortBy = ref("created_at");
  const isSortDirDesc = ref(true);

  // Filters
  const filters = ref({
    units: [],
    users: [],
    countries: [],
    municipalities: [],
    status: [],
    responsable: [],
    assigned: [],
    startDate: null,
    endDate: null,
    rems: null,
    sirh: null,
  });

  const filtersCount = computed(() => {
    let count = 0;
    if (filters.value.units.length) count++;
    if (filters.value.users.length) count++;
    if (filters.value.countries.length) count++;
    if (filters.value.municipalities.length) count++;
    if (filters.value.status.length) count++;
    if (filters.value.responsable.length) count++;
    if (filters.value.assigned.length) count++;
    if (filters.value.startDate || filters.value.endDate) count++;
    if (filters.value.rems !== null && filters.value.rems !== undefined) count++;
    if (filters.value.sirh !== null && filters.value.sirh !== undefined) count++;
    return count;
  });

  const clearSearchFilters = () => {
    filters.value = {
      units: [],
      users: [],
      countries: [],
      municipalities: [],
      status: [],
      responsable: [],
      assigned: [],
      startDate: null,
      endDate: null,
      rems: null,
      sirh: null,
    };
    currentPage.value = 1;
    refetchData();
  };

  const buildFilters = () => {
    // Normalize filter object on apply button
    filters.value = {
      units: filters.value.units || [],
      users: filters.value.users || [],
      countries: filters.value.countries || [],
      municipalities: filters.value.municipalities || [],
      status: filters.value.status || [],
      responsable: filters.value.responsable || [],
      assigned: filters.value.assigned || [],
      startDate: filters.value.startDate,
      endDate: filters.value.endDate,
      rems: filters.value.rems,
      sirh: filters.value.sirh,
    };
    currentPage.value = 1;
    refetchData();
  };

  // b-table plumbing
  const refListTable = ref(null);
  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });
  const perPageOptions = [10, 25, 50, 100];

  // Provider function (returns a Promise )
  const fetchList = () => {
    return new Promise((resolve, reject) => {
      const queryParams = {
        page: currentPage.value,
        per_page: perPage.value,
        sort_by: sortBy.value,
        sort_desc: isSortDirDesc.value ? 1 : 0,
        search: searchQuery.value,
        units: filters.value.units,
        users: filters.value.users,
        countries: filters.value.countries,
        municipalities: filters.value.municipalities,
        status_form: filters.value.status,
        responsable: filters.value.responsable,
        assigned: filters.value.assigned,
        date_from: filters.value.startDate,
        date_to: filters.value.endDate,
        rems: filters.value.rems,
        sirh: filters.value.sirh,
      };

      axios
        .get("/v1/security_self_audits", { params: queryParams })
        .then((response) => {
          const { data, meta } = response.data || {};
          totalList.value = meta?.total ?? 0;
          resolve(Array.isArray(data) ? data : []);
        })
        .catch((error) => {
          console.error("fetchList error", error);
          reject(error);
        });
    });
  };

  const refetchData = () => {
    if (refListTable.value) refListTable.value.refresh();
  };

  // Reactive reloads
  watch([currentPage, perPage, sortBy, isSortDirDesc], () => {
    refetchData();
  });

  watch([searchQuery], debounce(() => {
    refetchData();
  }, 500));

  // Date formatter
  const formatDateAssigned = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return new Intl.DateTimeFormat("default", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  return {
    // table
    tableColumns,
    perPage,
    currentPage,
    totalList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    refetchData,
    fetchList,
    formatDateAssigned,

    // filters and options
    filters,
    filtersCount,
    clearSearchFilters,
    buildFilters,
    unitOptions,
    userOptions,
    countryOptions,
    municipalityOptions,
  };
}
